import React, { useState, useEffect, useRef } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';

const ApiDropdown = ({ dataSource, initValue = null, placeholder, onSearch, selectedOption, ...props }) => {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState(dataSource);
    const [inputValue, setInputValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [defaultValue, setDefaultValue] = useState(initValue);

    // Keep a track of what we actually sent to Redux to prevent loops
    const lastSentQuery = useRef(initValue ? 'has_init_value' : '');

    const searchRef = useRef(onSearch);
    const selectedRef = useRef(selectedOption);
    
    useEffect(() => {
        searchRef.current = onSearch;
        selectedRef.current = selectedOption;
    }, [onSearch, selectedOption]);

    // Sync options when parent Redux data updates
    useEffect(() => {
        setOptions(dataSource || []);
        setLoading(false); 
    }, [dataSource]);

    // Sync initial baseline data safely
    useEffect(() => {
        setDefaultValue(initValue);
    }, [initValue]);

    // Helper function to safely update parent state without duplicating requests
    const triggerStateChange = (value, isImmediate = false) => {
        // If it's a string (from typing), keep it clean. If it's an object, use its label for tracking.
        const trackingValue = typeof value === 'string' ? value.trim() : (value?.label || '');
        
        if (lastSentQuery.current === trackingValue && !isImmediate) return;
        lastSentQuery.current = trackingValue;
        
        if (typeof selectedRef.current === 'function') {
            setLoading(true);
            // Pass the raw value forward (can be a string string or the option object!)
            selectedRef.current(value); 
        }
    };

    // Handle normal typing DEBOUNCE (Only runs if there is text)
    useEffect(() => {
        const queryText = (inputValue || '').trim();
        if (!queryText) return; // Do nothing here for empty text! Handled below.

        setLoading(true);
        const delayDebounceFn = setTimeout(() => {
            triggerStateChange(queryText);
        }, 400); 

        return () => clearTimeout(delayDebounceFn);
    }, [inputValue]);

    return (
        <Autocomplete
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            options={options}
            loading={loading}
            size="small"
            sx={{ ...(props.sx ?? {}), margin: 0, padding: 0 }}
            value={defaultValue}
            onChange={(event, newValue, reason) => {
                if (reason === 'clear' || newValue === null || newValue === '') {
                    setDefaultValue(null);
                    setInputValue('');
                    triggerStateChange('', true);
                    return;
                }
                setDefaultValue(newValue);
                triggerStateChange(newValue, true); 
            }}
            inputValue={inputValue}
            onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input' && (newInputValue || '').trim() === '') {
                    setInputValue('');
                    triggerStateChange('');
                    return;
                }

                setInputValue(newInputValue || '');
            }}
            filterOptions={(x) => x} 
            getOptionLabel={(option) => {
                if (!option) return "";
                if (typeof option === 'string') return option;
                return option.label || "";
            }}
            isOptionEqualToValue={(option, value) => {
                if (!option || !value) return false;
                return option.value === value?.value;
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={placeholder}
                    variant="outlined"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    error={Boolean(props?.errMsg ?? "")}
                    helperText={props?.errMsg ?? ""}
                />
            )}
            noOptionsText={props.noOptionsText ?? "No options"}
        />
    );
}

export default ApiDropdown;